<div class="eks-request-modal-backdrop" *ngIf="visible">
  <div class="eks-request-modal-card">
    <div class="eks-request-modal-header">
      <div class="eks-request-modal-title">
        <div class="eks-request-modal-icon">
          <i class="fa fa-cubes"></i>
        </div>
        <div>
          <h5>Request EKS Cluster</h5>
          <div class="eks-request-modal-subtitle">
            Create a new EKS cluster by selecting cluster details, network configuration, access, and tags.
          </div>
        </div>
      </div>
      <button
        type="button"
        class="eks-request-modal-close"
        aria-label="Close popup"
        [disabled]="loading.submit || loading.pricing || loading.createSecurityGroup"
        (click)="requestClose()"
      >
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div class="eks-request-steps">
      <div
        class="eks-request-step"
        *ngFor="let item of steps"
        [class.active]="step === item.id"
        [class.done]="step > item.id"
      >
        <span class="eks-request-step__badge">
          <i *ngIf="step > item.id" class="fa fa-check"></i>
          <ng-container *ngIf="step <= item.id">{{ item.id }}</ng-container>
        </span>
        <span class="eks-request-step__label">{{ item.label }}</span>
      </div>
    </div>

    <div #eksRequestModalBody class="eks-request-modal-body">
      <div *ngIf="step === 1">
        <h5>Cluster details</h5>
        <p class="eks-request-copy">Name the cluster and add the business reason for this request.</p>
        <div class="eks-request-context">
          <div class="eks-request-context-chip">
            <span>Account</span>
            <strong>{{ accountId || "-" }}</strong>
          </div>
          <div class="eks-request-context-chip">
            <span>Region</span>
            <strong>{{ region || "-" }}</strong>
          </div>
        </div>
        <div class="eks-request-grid">
          <div class="eks-request-field eks-request-field--full">
            <label>Cluster Name <span class="required-star">*</span></label>
            <input class="form-control" [(ngModel)]="form.cluster_name" placeholder="Enter cluster name" />
          </div>
          <div class="eks-request-field eks-request-field--full">
            <label>Launch Justification <span class="required-star">*</span></label>
            <textarea
              class="form-control"
              [(ngModel)]="form.launch_justification"
              rows="3"
              placeholder="Enter launch justification"
            ></textarea>
          </div>
        </div>
      </div>

      <div *ngIf="step === 2">
        <h5>Network</h5>
        <p class="eks-request-copy">Choose the VPC, subnets, and security groups for the cluster control plane.</p>
        <div class="eks-request-grid">
          <div class="eks-request-field eks-request-field--full">
            <label>VPC Id <span class="required-star">*</span> <span *ngIf="loading.vpcs" class="tiny-loader"></span></label>
            <ng-select
              #requestVpcSelect
              class="instance-type-select"
              [items]="options.vpcs"
              bindLabel="vpc_id"
              bindValue="vpc_id"
              [(ngModel)]="form.vpc_id"
              (change)="onVpcChange()"
              (open)="onDropdownOpen(requestVpcSelect, 'request')"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select"
              notFoundText="No VPC found"
            >
              <ng-template ng-option-tmp let-item="item">
                {{ item.vpc_id }} {{ item.vpc_name ? "- (" + item.vpc_name + ")" : "" }}
              </ng-template>
            </ng-select>
          </div>
          <div class="eks-request-field">
            <div class="eks-field-label-row">
              <label class="mb-0">
                Subnet Ids <span class="required-star">*</span>
                <span *ngIf="loading.subnets" class="tiny-loader"></span>
              </label>
            </div>
            <ng-select
              #requestSubnetSelect
              class="instance-type-select"
              [items]="options.subnets"
              bindLabel="subnet_id"
              bindValue="subnet_id"
              [(ngModel)]="form.subnet_ids"
              (open)="onDropdownOpen(requestSubnetSelect, 'request')"
              [multiple]="true"
              [searchable]="true"
              [clearable]="true"
              [disabled]="!form.vpc_id"
              placeholder="Select"
              notFoundText="No subnet found"
            >
              <ng-template ng-option-tmp let-item="item">
                {{ item.subnet_id }} {{ item.subnet_name ? "- (" + item.subnet_name + ")" : "" }}
              </ng-template>
            </ng-select>
          </div>
          <div class="eks-request-field">
            <div class="eks-field-label-row d-flex align-items-center justify-content-between">
              <label class="mb-0">
                Security Group Ids <span class="required-star">*</span>
                <span *ngIf="loading.securityGroups" class="tiny-loader"></span>
              </label>
              <button
                type="button"
                class="btn btn-light btn-sm"
                [title]="showCreateSecurityGroupForm ? 'Close create security group form' : 'Create security group'"
                [disabled]="loading.createSecurityGroup"
                (click)="showCreateSecurityGroupForm ? closeCreateSecurityGroupForm() : openCreateSecurityGroupForm()"
              >
                <i class="fa" [ngClass]="showCreateSecurityGroupForm ? 'fa-times' : 'fa-plus'"></i>
                <span>{{ showCreateSecurityGroupForm ? "Close" : "Create Security Group" }}</span>
              </button>
            </div>
            <ng-select
              #requestSecurityGroupSelect
              class="instance-type-select"
              [items]="options.securityGroups"
              bindLabel="security_group_name"
              bindValue="security_group_id"
              [(ngModel)]="form.security_group_ids"
              (open)="onDropdownOpen(requestSecurityGroupSelect, 'request')"
              [multiple]="true"
              [searchable]="true"
              [clearable]="true"
              dropdownPosition="bottom"
              [disabled]="!form.vpc_id"
              placeholder="Select"
              notFoundText="No security group found"
            >
              <ng-template ng-option-tmp let-item="item">
                <div>
                  <div>{{ item.security_group_name || item.group_name || item.security_group_id || item.group_id }}</div>
                  <small class="text-muted">{{ item.security_group_id || item.group_id }}</small>
                </div>
              </ng-template>
            </ng-select>
          </div>
        </div>
      </div>

      <div *ngIf="step === 3">
        <h5>Access and version</h5>
        <p class="eks-request-copy">Select the cluster role, Kubernetes version, and endpoint access mode.</p>
        <div class="eks-request-grid">
          <div class="eks-request-field eks-request-field--full">
            <label>Cluster Role ARN <span class="required-star">*</span> <span *ngIf="loading.clusterRoles" class="tiny-loader"></span></label>
            <ng-select
              #requestClusterRoleSelect
              class="instance-type-select"
              [items]="options.clusterRoles"
              bindLabel="role_arn"
              bindValue="role_arn"
              [(ngModel)]="form.cluster_role_arn"
              (open)="onDropdownOpen(requestClusterRoleSelect, 'request')"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select"
              notFoundText="No cluster role found"
            >
              <ng-template ng-option-tmp let-item="item">
                {{ item.role_name }} - {{ item.role_arn }}
              </ng-template>
            </ng-select>
          </div>
          <div class="eks-request-field eks-request-field--full">
            <label>Kubernetes Version <span class="required-star">*</span> <span *ngIf="loading.kubernetesVersions" class="tiny-loader"></span></label>
            <ng-select
              #requestVersionSelect
              class="instance-type-select"
              [items]="options.kubernetesVersions"
              bindLabel="cluster_version"
              bindValue="cluster_version"
              [(ngModel)]="form.kubernetes_version"
              (open)="onDropdownOpen(requestVersionSelect, 'request')"
              [searchable]="true"
              [clearable]="true"
              placeholder="Select"
              notFoundText="No Kubernetes version found"
            >
              <ng-template ng-option-tmp let-item="item">
                {{ item.cluster_version }} {{ item.default_version ? "(default)" : "" }} - {{ item.version_status }}
              </ng-template>
            </ng-select>
          </div>
          <div class="eks-request-field">
            <label>Endpoint Public Access <span class="required-star">*</span></label>
            <ng-select #requestPublicAccessSelect class="instance-type-select" [(ngModel)]="form.endpoint_public_access" (open)="onDropdownOpen(requestPublicAccessSelect, 'request')" [searchable]="false" [clearable]="false" placeholder="Select">
              <ng-option [value]="true">true</ng-option>
              <ng-option [value]="false">false</ng-option>
            </ng-select>
          </div>
          <div class="eks-request-field">
            <label>Endpoint Private Access <span class="required-star">*</span></label>
            <ng-select #requestPrivateAccessSelect class="instance-type-select" [(ngModel)]="form.endpoint_private_access" (open)="onDropdownOpen(requestPrivateAccessSelect, 'request')" [searchable]="false" [clearable]="false" placeholder="Select">
              <ng-option [value]="true">true</ng-option>
              <ng-option [value]="false">false</ng-option>
            </ng-select>
          </div>
        </div>
      </div>

      <div *ngIf="step === 4">
        <h5>Tags</h5>
        <p class="eks-request-copy">Add optional tags that should be applied to the EKS request payload.</p>
        <div *ngFor="let t of form.tags; let i = index" class="tag-row">
          <input class="form-control" placeholder="key" [(ngModel)]="t.key" />
          <input class="form-control" placeholder="value" [(ngModel)]="t.value" />
          <button type="button" class="btn btn-outline-secondary btn-sm tag-remove" (click)="removeTag(i)">X</button>
        </div>
        <button type="button" class="btn btn-light btn-sm mt-2 add-tag-btn" (click)="addTag()">
          <i class="fa fa-plus"></i> Add Tag
        </button>
      </div>

      <div *ngIf="step === 5">
        <h5>Review</h5>
        <p class="eks-request-copy">Review the request and pricing estimate before submitting.</p>
        <div *ngIf="loading.pricing" class="mapping-box">Fetching pricing...</div>
        <div *ngIf="!loading.pricing && pricingError" class="mapping-box text-danger">{{ pricingError }}</div>
        <div class="eks-review-grid" *ngIf="!loading.pricing">
          <div><span>Cluster</span><strong>{{ form.cluster_name || "-" }}</strong></div>
          <div><span>Version</span><strong>{{ form.kubernetes_version || "-" }}</strong></div>
          <div><span>VPC</span><strong>{{ form.vpc_id || "-" }}</strong></div>
          <div><span>Subnets</span><strong>{{ form.subnet_ids?.join(", ") || "-" }}</strong></div>
          <div><span>Security Groups</span><strong>{{ form.security_group_ids?.join(", ") || "-" }}</strong></div>
          <div><span>Hourly Cost</span><strong>{{ (clusterPricing?.hourly_cost_usd ?? "-") + " " + (clusterPricing?.currency || "USD") }}</strong></div>
          <div><span>Monthly Cost</span><strong>{{ (clusterPricing?.monthly_cost_usd ?? "-") + " " + (clusterPricing?.currency || "USD") }}</strong></div>
        </div>
      </div>

      <div *ngIf="formError" class="alert alert-warning mb-0 mt-3">{{ formError }}</div>
      <div *ngIf="saveError" class="alert alert-danger mb-0 mt-3">{{ saveError }}</div>
    </div>

    <div class="eks-request-modal-footer">
      <button
        type="button"
        class="btn btn-light"
        (click)="step === 1 ? requestClose() : back()"
        [disabled]="loading.submit || loading.pricing || loading.createSecurityGroup"
      >
        <ng-container *ngIf="step === 1">Cancel</ng-container>
        <ng-container *ngIf="step > 1"><i class="fa fa-arrow-left"></i> Back</ng-container>
      </button>

      <div class="eks-request-footer-meta">Step {{ step }} of {{ steps.length }}</div>

      <button
        *ngIf="step < steps.length"
        type="button"
        class="btn eks-footer-btn eks-footer-btn--primary"
        (click)="next()"
        [disabled]="loading.pricing || loading.createSecurityGroup"
      >
        Next <i class="fa fa-arrow-right"></i>
      </button>
      <button
        *ngIf="step === steps.length"
        type="button"
        class="btn eks-footer-btn eks-footer-btn--accent"
        (click)="submitRequest()"
        [disabled]="loading.submit || loading.pricing || loading.createSecurityGroup || !clusterPricing || !!pricingError"
      >
        <span *ngIf="loading.submit" class="spinner-border spinner-border-sm me-2"></span>
        {{ loading.submit ? "Submitting..." : "Submit Request" }}
      </button>
    </div>
  </div>

  <div *ngIf="showCreateSecurityGroupForm" class="sg-modal-backdrop">
    <div class="sg-modal">
      <div class="sg-modal__header">
        <div class="sg-modal__title-wrap">
          <div class="sg-modal__badge">
            <i class="fa fa-shield"></i>
          </div>
          <div class="sg-create-panel__title">Create Security Group</div>
        </div>
        <button
          type="button"
          class="sg-modal__close"
          [disabled]="loading.createSecurityGroup"
          title="Close"
          (click)="closeCreateSecurityGroupForm()"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div #securityGroupModalBody class="sg-modal__body">
        <div class="sg-stepper">
          <div
            *ngFor="let stepLabel of createSecurityGroupStepLabels; let i = index"
            class="sg-stepper__item"
            [ngClass]="{
              'is-active': createSecurityGroupStep === i + 1,
              'is-complete': createSecurityGroupStep > i + 1
            }"
          >
            <div class="sg-stepper__circle">
              <ng-container *ngIf="createSecurityGroupStep > i + 1; else sgStepNumber">
                <i class="fa fa-check"></i>
              </ng-container>
              <ng-template #sgStepNumber>{{ i + 1 }}</ng-template>
            </div>
            <div class="sg-stepper__label">{{ stepLabel }}</div>
          </div>
        </div>

        <div *ngIf="createSecurityGroupStep === 1" class="sg-create-section">
          <div class="sg-step-panel__title">Basic details</div>
          <div class="sg-step-panel__subtitle">
            The security group's name and VPC scope. Description is required by AWS.
          </div>

          <div class="sg-create-grid">
            <div class="form-item">
              <label>Name <span class="required-star">*</span></label>
              <input
                class="form-control"
                [ngClass]="{
                  'field-invalid':
                    createSecurityGroupSubmitAttempted &&
                    !(createSecurityGroupForm.group_name || '').toString().trim()
                }"
                [(ngModel)]="createSecurityGroupForm.group_name"
                placeholder="e.g. eks-control-plane-sg"
              />
              <div
                *ngIf="
                  createSecurityGroupSubmitAttempted &&
                  !(createSecurityGroupForm.group_name || '').toString().trim()
                "
                class="field-error"
              >
                Security group name is required.
              </div>
            </div>

            <div class="form-item sg-create-grid__full">
              <label>Description <span class="required-star">*</span></label>
              <input
                class="form-control"
                [ngClass]="{
                  'field-invalid':
                    createSecurityGroupSubmitAttempted &&
                    !(createSecurityGroupForm.description || '').toString().trim()
                }"
                [(ngModel)]="createSecurityGroupForm.description"
                placeholder="What this SG protects"
              />
              <div
                *ngIf="
                  createSecurityGroupSubmitAttempted &&
                  !(createSecurityGroupForm.description || '').toString().trim()
                "
                class="field-error"
              >
                Description is required.
              </div>
            </div>

            <div class="form-item sg-create-grid__full">
              <label>VPC <span class="required-star">*</span></label>
              <ng-select
                #createSecurityGroupVpcSelect
                class="instance-type-select"
                [ngClass]="{
                  'invalid-select':
                    createSecurityGroupSubmitAttempted &&
                    !(createSecurityGroupForm.vpc_id || '').toString().trim()
                }"
                [items]="options.vpcs"
                bindLabel="vpc_id"
                bindValue="vpc_id"
                [(ngModel)]="createSecurityGroupForm.vpc_id"
                (open)="onDropdownOpen(createSecurityGroupVpcSelect, 'securityGroup')"
                [searchable]="true"
                [clearable]="true"
                placeholder="Select VPC"
                notFoundText="No VPC found"
              >
                <ng-template ng-option-tmp let-item="item">
                  {{ getCreateSecurityGroupVpcLabel(item.vpc_id) }}
                </ng-template>
              </ng-select>
              <div
                *ngIf="
                  createSecurityGroupSubmitAttempted &&
                  !(createSecurityGroupForm.vpc_id || '').toString().trim()
                "
                class="field-error"
              >
                VPC is required.
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="createSecurityGroupStep === 2" class="sg-create-section">
          <div class="sg-step-panel__title">Inbound rules</div>
          <div class="sg-step-panel__subtitle">
            Allow traffic INTO resources attached to this group. Critical ports (22 SSH, 3389 RDP)
            open to 0.0.0.0/0 are flagged.
          </div>

          <div class="sg-rule-editor">
            <div class="sg-rule-editor__header">
              <span>Protocol</span>
              <span>Port</span>
              <span>Source</span>
              <span>Description</span>
              <span></span>
            </div>

            <div
              *ngFor="let rule of createSecurityGroupForm.inbound_rules; let i = index"
              class="sg-rule-block"
            >
              <div class="sg-rule-row">
                <div class="sg-rule-field">
                  <label class="sg-rule-field__mobile-label">Protocol</label>
                  <select
                    class="form-control"
                    [(ngModel)]="rule.protocol"
                    (ngModelChange)="onCreateSecurityGroupRuleProtocolChange(rule)"
                  >
                    <option *ngFor="let protocol of createSecurityGroupProtocolOptions" [value]="protocol">
                      {{ protocol }}
                    </option>
                  </select>
                </div>

                <div class="sg-rule-field">
                  <label class="sg-rule-field__mobile-label">Port</label>
                  <input
                    class="form-control"
                    [ngClass]="{
                      'field-invalid':
                        createSecurityGroupSubmitAttempted &&
                        !isCreateSecurityGroupRuleValid(rule, 'inbound')
                    }"
                    [(ngModel)]="rule.port"
                    [disabled]="!usesCreateSecurityGroupRulePort(rule.protocol)"
                    [placeholder]="usesCreateSecurityGroupRulePort(rule.protocol) ? '443' : '-'"
                  />
                </div>

                <div class="sg-rule-field">
                  <label class="sg-rule-field__mobile-label">Source</label>
                  <input
                    class="form-control"
                    [ngClass]="{
                      'field-invalid':
                        createSecurityGroupSubmitAttempted &&
                        !isCreateSecurityGroupRuleValid(rule, 'inbound')
                    }"
                    [(ngModel)]="rule.target"
                    placeholder="0.0.0.0/0"
                  />
                </div>

                <div class="sg-rule-field">
                  <label class="sg-rule-field__mobile-label">Description</label>
                  <input
                    class="form-control"
                    [ngClass]="{
                      'field-invalid':
                        createSecurityGroupSubmitAttempted &&
                        !isCreateSecurityGroupRuleValid(rule, 'inbound')
                    }"
                    [(ngModel)]="rule.description"
                    placeholder="HTTPS"
                  />
                </div>

                <button
                  type="button"
                  class="sg-rule-remove"
                  (click)="removeCreateSecurityGroupRule('inbound', i)"
                  title="Remove inbound rule"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>

              <div
                *ngIf="
                  createSecurityGroupSubmitAttempted &&
                  !isCreateSecurityGroupRuleValid(rule, 'inbound')
                "
                class="field-error"
              >
                {{ getCreateSecurityGroupRuleValidationMessage(rule, 'inbound') }}
              </div>

              <div *ngIf="isCreateSecurityGroupRuleFlagged(rule)" class="sg-rule-warning">
                This inbound rule opens SSH or RDP access to the world.
              </div>
            </div>
          </div>

          <button type="button" class="sg-add-rule-btn" (click)="addCreateSecurityGroupRule('inbound')">
            <i class="fa fa-plus"></i> Add inbound rule
          </button>
        </div>

        <div *ngIf="createSecurityGroupStep === 3" class="sg-create-section">
          <div class="sg-step-panel__title">Outbound rules</div>
          <div class="sg-step-panel__subtitle">
            Allow traffic OUT FROM resources attached to this group.
          </div>

          <div class="sg-rule-editor">
            <div class="sg-rule-editor__header">
              <span>Protocol</span>
              <span>Port</span>
              <span>Destination</span>
              <span>Description</span>
              <span></span>
            </div>

            <div
              *ngFor="let rule of createSecurityGroupForm.outbound_rules; let i = index"
              class="sg-rule-block"
            >
              <div class="sg-rule-row">
                <div class="sg-rule-field">
                  <label class="sg-rule-field__mobile-label">Protocol</label>
                  <select
                    class="form-control"
                    [(ngModel)]="rule.protocol"
                    (ngModelChange)="onCreateSecurityGroupRuleProtocolChange(rule)"
                  >
                    <option *ngFor="let protocol of createSecurityGroupProtocolOptions" [value]="protocol">
                      {{ protocol }}
                    </option>
                  </select>
                </div>

                <div class="sg-rule-field">
                  <label class="sg-rule-field__mobile-label">Port</label>
                  <input
                    class="form-control"
                    [ngClass]="{
                      'field-invalid':
                        createSecurityGroupSubmitAttempted &&
                        !isCreateSecurityGroupRuleValid(rule, 'outbound')
                    }"
                    [(ngModel)]="rule.port"
                    [disabled]="!usesCreateSecurityGroupRulePort(rule.protocol)"
                    [placeholder]="usesCreateSecurityGroupRulePort(rule.protocol) ? '443' : '-'"
                  />
                </div>

                <div class="sg-rule-field">
                  <label class="sg-rule-field__mobile-label">Destination</label>
                  <input
                    class="form-control"
                    [ngClass]="{
                      'field-invalid':
                        createSecurityGroupSubmitAttempted &&
                        !isCreateSecurityGroupRuleValid(rule, 'outbound')
                    }"
                    [(ngModel)]="rule.target"
                    placeholder="0.0.0.0/0"
                  />
                </div>

                <div class="sg-rule-field">
                  <label class="sg-rule-field__mobile-label">Description</label>
                  <input
                    class="form-control"
                    [ngClass]="{
                      'field-invalid':
                        createSecurityGroupSubmitAttempted &&
                        !isCreateSecurityGroupRuleValid(rule, 'outbound')
                    }"
                    [(ngModel)]="rule.description"
                    placeholder="Allow all outbound"
                  />
                </div>

                <button
                  type="button"
                  class="sg-rule-remove"
                  (click)="removeCreateSecurityGroupRule('outbound', i)"
                  title="Remove outbound rule"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>

              <div
                *ngIf="
                  createSecurityGroupSubmitAttempted &&
                  !isCreateSecurityGroupRuleValid(rule, 'outbound')
                "
                class="field-error"
              >
                {{ getCreateSecurityGroupRuleValidationMessage(rule, 'outbound') }}
              </div>
            </div>
          </div>

          <button type="button" class="sg-add-rule-btn" (click)="addCreateSecurityGroupRule('outbound')">
            <i class="fa fa-plus"></i> Add outbound rule
          </button>
        </div>

        <div *ngIf="createSecurityGroupStep === 4" class="sg-create-section">
          <div class="sg-step-panel__title">Tag the security group</div>

          <div class="form-item sg-tag-field">
            <label>Environment</label>
            <ng-select
              #createSecurityGroupEnvironmentSelect
              class="instance-type-select"
              [clearable]="false"
              [searchable]="false"
              [(ngModel)]="createSecurityGroupForm.environment"
              (open)="onDropdownOpen(createSecurityGroupEnvironmentSelect, 'securityGroup')"
            >
              <ng-option
                *ngFor="let environment of createSecurityGroupEnvironmentOptions"
                [value]="environment"
              >
                {{ environment }}
              </ng-option>
            </ng-select>
          </div>
        </div>

        <div *ngIf="createSecurityGroupStep === 5" class="sg-create-section">
          <div class="sg-review-banner">
            The security group will be created first, then the inbound and outbound rules will be applied.
          </div>

          <div class="sg-review-card">
            <div class="sg-review-row">
              <span>Name</span>
              <strong>{{ createSecurityGroupForm.group_name || "-" }}</strong>
            </div>
            <div class="sg-review-row">
              <span>Description</span>
              <strong>{{ createSecurityGroupForm.description || "-" }}</strong>
            </div>
            <div class="sg-review-row">
              <span>VPC</span>
              <strong>{{ getCreateSecurityGroupVpcLabel(createSecurityGroupForm.vpc_id) }}</strong>
            </div>
            <div class="sg-review-row">
              <span>Inbound rules</span>
              <strong>{{ getCreateSecurityGroupRuleCountLabel('inbound') }}</strong>
            </div>
            <div class="sg-review-row">
              <span>Outbound rules</span>
              <strong>{{ getCreateSecurityGroupRuleCountLabel('outbound') }}</strong>
            </div>
            <div class="sg-review-row">
              <span>Environment</span>
              <strong>{{ createSecurityGroupForm.environment || "-" }}</strong>
            </div>
          </div>

          <div class="sg-review-rules">
            <div class="sg-review-rules__title">Inbound:</div>
            <ul>
              <li *ngFor="let rule of createSecurityGroupForm.inbound_rules">
                {{ formatCreateSecurityGroupRuleForReview(rule, 'inbound') }}
              </li>
            </ul>

            <div class="sg-review-rules__title">Outbound:</div>
            <ul>
              <li *ngFor="let rule of createSecurityGroupForm.outbound_rules">
                {{ formatCreateSecurityGroupRuleForReview(rule, 'outbound') }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="sg-modal__footer">
        <div class="sg-create-actions">
          <button
            *ngIf="createSecurityGroupStep === 1"
            type="button"
            class="btn btn-light"
            [disabled]="loading.createSecurityGroup"
            (click)="closeCreateSecurityGroupForm()"
          >
            Cancel
          </button>
          <button
            *ngIf="createSecurityGroupStep > 1"
            type="button"
            class="btn btn-light"
            [disabled]="loading.createSecurityGroup"
            (click)="previousCreateSecurityGroupStep()"
          >
            &larr; Back
          </button>

          <div class="sg-create-actions__meta">
            Step {{ createSecurityGroupStep }} of {{ createSecurityGroupStepLabels.length }}
          </div>

          <button
            *ngIf="createSecurityGroupStep < createSecurityGroupStepLabels.length"
            type="button"
            class="btn btn-primary sg-next-btn"
            [disabled]="loading.createSecurityGroup"
            (click)="nextCreateSecurityGroupStep()"
          >
            Next &rarr;
          </button>
          <button
            *ngIf="createSecurityGroupStep === createSecurityGroupStepLabels.length"
            type="button"
            class="btn sg-create-submit-btn"
            [disabled]="loading.createSecurityGroup"
            (click)="submitCreateSecurityGroup()"
          >
            <span *ngIf="loading.createSecurityGroup" class="spinner-border spinner-border-sm me-2"></span>
            {{ loading.createSecurityGroup ? "Creating..." : "Create security group" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
